<template>
    <UniversalPopup ref="universalPopupRef" @onConfirm="onExit" />
    <n-dialog v-model:visible="visible" fullscreen :width="'400px'" :modal="false">
        <div class="upload-file-content" v-show="visible" :class="{'desktop-upload-file-content': isDesktop , 'mobile-upload-file-content': !isDesktop}">
            <div class="upload-file-popup-content">
                <div class="upload-file-popup-content-header">
                    <i class="iconfont icon-icon_close" @click="onExitDialog" v-if="!isExternalSource"/>
                </div>
                <div class="upload-file-content-top">
                    <i class="iconfont icon-icon_pdf6"></i>
                    <span class="file-status-title">{{ $t("editor_home_upload_progress_title_toWork") }}</span>
                    <div class="file-name-container">
                        <i class="iconfont icon-icon_paperclip"/>
                        <span class="upload-file-content-top-title">{{ fileName ? fileName : $t('editor_home_upload_progress_bottom_nameing') }}</span>
                    </div>
                </div>
                
                <div class="upload-file-content-center">
                    <!-- <Progress :percentage="percentage" :size="180" :strokeWidth="10" color="#3B82F6" /> -->
                    <div class="upload-file-content-center-progress">
                        <div class="progress-bar-container">
                            <div 
                                class="progress-bar-fill" 
                                :style="{ width: Math.floor(showPercentage) + '%' }"
                            >
                                <div class="progress-bar-shine"></div>
                            </div>
                        </div>
                        <div class="progress-title-container">
                            <div class="progress-title">{{ getProgressTitle() }}</div>
                            <div class="progress-percentage">{{ Math.floor(showPercentage) }}%</div>
                        </div>
                    </div>
                    <div class="upload-file-content-center-progress-bottom">
                        <i class="iconfont icon-icon_hint1" />
                        <span class="bottom-title">{{ $t("editor_home_upload_progress_bottom_tips") }}</span>
                    </div>
                    <div class="upload-file-content-center-progress-bottom-tips">
                        <i class="iconfont icon-icon_secure3" />
                        <span class="bottom-tips-title">{{ $t("editor_home_upload_progress_bottom_tips") }}</span>
                    </div>
                    <!-- <div class="upload-file-content-center-progress-title">
                        {{ $t("editor_home_upload_progress_title") }}
                    </div>
                    <div class="upload-file-content-center-progress-sub-title">
                        {{ $t("editor_home_upload_progress_sub_title") }}
                    </div>
                    <div class="upload-file-content-center-progress-button" @click="onCancel">
                        {{ $t("editor_home_upload_progress_button") }}
                    </div>
                    <div class="upload-file-content-center-progress-tip">
                        {{ $t("editor_home_upload_progress_tip") }}
                    </div> -->
                </div>
            </div>
        </div>
    </n-dialog>
    
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import UniversalPopup from '~/components/Dialog/UniversalPopup/index.vue'
import Progress from '~/components/Progress/index.vue'
import NDialog from '~/components/NDialog/index.vue'
import UploadServer from '~/services/UploadServer'
import eventBus from 'vue3-eventbus'
import Toast from '~/utils/toast'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { saveLargeFileToIndexedDB } from '~/utils/indexedDB'
import { EventRenderProgressMessage, EventHomeMessage} from '~/config/event'
const { t } = useI18n()

const viewport = useViewport()
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))
const visible = ref(false)
const emit = defineEmits(['onCancel', 'onConfirm']);
const showPercentage = ref(0)
const fileName = ref('')
const universalPopupRef = ref<InstanceType<typeof UniversalPopup>>()
const fileInfo = ref<File | null>(null)

let cancelUpload: (() => void) | null = null

const router = useRouter()

// ✅ 进度配置
const PROGRESS_CONFIG = {
  UPLOAD_END: 30,           // 上传阶段：0% → 30%
  DOWNLOAD_END: 50,         // 下载阶段：30% → 50%
  WASM_END: 90,             // WASM 加载：50% → 90%
  COMPLETE_SPEED: 0.5,
  SMOOTH_FACTOR: 0.15,
}

// ✅ 进度状态
let currentDisplayProgress = 0
let targetProgress = 0
let smoothAnimationFrame: number | null = null
let isDownCompleted = false
let isPDFRenderComplete = false
let isWasmLoadComplete = false  // ✅ 新增：WASM 加载完成标志
let isCompleting = ref(false)

// ✅ 设置目标进度（只增不减）
function setTargetProgress(progress: number) {
  if (progress > targetProgress) {
    targetProgress = progress
    console.log('[Progress] 目标进度:', progress.toFixed(2), '%')
  }
}

// ✅ 启动假下载进度（慢速增长到 50%）
let fakeProgressTimer: number | null = null

function startFakeDownloadProgress() {
  if (fakeProgressTimer) return

  console.log('[Progress] 启动假进度，上限: 50%')

  fakeProgressTimer = window.setInterval(() => {
    // 如果已经完成或接近 50%，停止假进度
    if (isDownCompleted || currentDisplayProgress >= 49) {
      stopFakeDownloadProgress()
      return
    }

    // 慢速增长：每100ms增加0.1-0.3%
    const increment = 0.1 + Math.random() * 0.2
    const newProgress = Math.min(currentDisplayProgress + increment, 49)

    setTargetProgress(newProgress)
  }, 100)
}

function stopFakeDownloadProgress() {
  if (fakeProgressTimer) {
    clearInterval(fakeProgressTimer)
    fakeProgressTimer = null
    console.log('[Progress] 停止假进度')
  }
}

// ✅ 平滑进度更新
function startSmoothProgress() {
  if (smoothAnimationFrame) return
  const animate = () => {
    if (targetProgress > currentDisplayProgress) {
      const diff = targetProgress - currentDisplayProgress
      currentDisplayProgress += diff * PROGRESS_CONFIG.SMOOTH_FACTOR
      showPercentage.value = Math.min(100, Math.max(0, currentDisplayProgress))
    }
    smoothAnimationFrame = requestAnimationFrame(animate)
  }
  smoothAnimationFrame = requestAnimationFrame(animate)
}

// ✅ 停止平滑进度
function stopSmoothProgress() {
  if (smoothAnimationFrame) {
    cancelAnimationFrame(smoothAnimationFrame)
    smoothAnimationFrame = null
  }
}

const getProgressTitle = ()=>{
    if (fileInfo.value) {
        if (showPercentage.value < 5) {
            return t('editor_home_upload_progress_title_step_1')
        } else if (showPercentage.value < 30) {
            return t('editor_home_upload_progress_title_step_2')
        } else if (showPercentage.value < 35) {
            return t('editor_home_upload_progress_title_step_3')
        } else if (showPercentage.value < 50) {
            return t('editor_home_upload_progress_title_step_5')
        } else if (showPercentage.value == 50) {
            return t('editor_home_upload_progress_title_step_4')
        } else if (showPercentage.value < 90) {
            // ✅ WASM 加载阶段（50% 到 90%）
            return t('editor_home_upload_progress_title_step_wasm')
        } else if (showPercentage.value < 100) {
            return t('editor_home_upload_progress_title_step_6')
        } else if (showPercentage.value == 100) {
            return t('editor_home_upload_progress_title_step_7')
        }
    } else {
        if (showPercentage.value < 35) {
            return t('editor_home_upload_progress_title_step_3')
        } else if (showPercentage.value < 50) {
            return t('editor_home_upload_progress_title_step_5')
        } else if (showPercentage.value < 90) {
            // ✅ WASM 加载阶段（50% 到 90%）
            return t('editor_home_upload_progress_title_step_wasm')
        } else if (showPercentage.value < 100) {
            return t('editor_home_upload_progress_title_step_6')
        } else if (showPercentage.value == 100) {
            return t('editor_home_upload_progress_title_step_7')
        }
    }
    return t('editor_home_upload_progress_title_step_1')
}

const onExitDialog = ()=>{
    universalPopupRef.value?.open({
        title: t('editor_popup_title'),
        subTitle: t('editor_popup_subTitle'),
        btn1: t('editor_popup_cancel'),
        btn2: t('editor_popup_confirm')
    })
}

const onExit = ()=>{
    onCancel()
    router.replace({ path: '/' });
}

const onCancel = ()=>{
    console.log('取消')
    if (cancelUpload) {
        cancelUpload()
        cancelUpload = null
    }
    visible.value = false
    showPercentage.value = 0
    isPDFRenderComplete = false
    isDownCompleted = false
    isWasmLoadComplete = false  // ✅ 重置 WASM 加载状态
    emit('onCancel')
}

const uploadFile = (file: File, md5?: string)=>{
    const formData = new FormData()
    formData.append('localFile', file)
    const req = { data: formData }
    console.log("handleBeforeUpload===> 上传", file)

    fileName.value = file.name
    const uploadResult = UploadServer.getUploadFile(req)
    cancelUpload = uploadResult.cancel
    return uploadResult.promise
      .then(async (data: any)=>{
        if (data.code === "1") {
          const operateRecordId = data.data.operateRecordId || data.data.sourceFileId
          await uploadFileHandler(file, operateRecordId)
          return data
        }else {
          Toast(data.message)
          throw new Error(data.message || 'Upload failed')
        }
      }).catch((error)=>{
        if (error.message !== 'Request aborted') {
          Toast(error.message)
        }
        throw error
      }).finally(()=>{
        cancelUpload = null
      })
}

const pushEditPage = (operateRecordId: number) =>{
    router.push({ path: '/editor', query: { operateRecordId: operateRecordId } })
}

const sourceType = ref<string>('')
const open = async (file?: File, md5?: string, source?: string)=>{
    if (visible.value) return

    visible.value = true
    sourceType.value = source || ''

    // ✅ 重置进度状态（包括所有完成标志）
    currentDisplayProgress = 0
    targetProgress = 0
    showPercentage.value = 0
    fileInfo.value = null
    isCompleting.value = false
    isPDFRenderComplete = false
    isDownCompleted = false
    isWasmLoadComplete = false

    // ✅ 启动平滑进度
    startSmoothProgress()

    if (file) {
        fileInfo.value = file as File
        try {
          await uploadFile(file, md5)
          // ✅ 上传完成后，启动下载阶段的假进度
          console.log('[Progress] 上传完成，启动下载阶段')
          startFakeDownloadProgress()
        } catch (error: any) {
          console.error('Upload failed', error)
          // ✅ 使用错误处理函数
          if (error?.message !== 'Request aborted') {
            handleUploadError(error as Error)
          }
        }
    } else {
        // ✅ 没有文件时，直接启动下载阶段
        console.log('[Progress] 无文件上传，直接启动下载阶段')
        startFakeDownloadProgress()
    }
}

const close = async () => {
    visible.value = false
    showPercentage.value = 0
    stopSmoothProgress()
    isPDFRenderComplete = false
    isDownCompleted = false
    isWasmLoadComplete = false  // ✅ 重置 WASM 加载状态
    // ✅ 修复：只关闭弹窗，不跳转页面
    // 只有用户主动取消时才跳转首页
}

// ✅ 错误处理函数
const handleUploadError = (error: Error) => {
    console.error('[UploadFilePopup] 上传错误:', error)

    // 显示错误提示（持续 3 秒）
    Toast(error.message, 'fail', 2)

    // 3 秒后自动关闭弹窗
    setTimeout(() => {
        visible.value = false
        showPercentage.value = 0
        isPDFRenderComplete = false
        isDownCompleted = false
        isWasmLoadComplete = false
    }, 3000)
}

const handleUploadProgress = (data: {progress: number}) => {
    console.log("[Progress] 上传:", data.progress)
    const rawProgress = Math.floor((data.progress >= 1 ? 0.99 : data.progress) * 100)
    // ✅ 上传进度映射到 0-30%
    const uploadProgress = Math.min((rawProgress / 100) * PROGRESS_CONFIG.UPLOAD_END, PROGRESS_CONFIG.UPLOAD_END)
    setTargetProgress(uploadProgress)
}

const uploadFileHandler = async (file: any, operateRecordId: number) => {
    if (process.client) {
        try {
            localStorage.removeItem('editor_ocr_status');
        } catch (e) {
            console.error('清除 OCR 状态失败', e);
        }

        // 直接存储 Blob，不转换为 Base64
        // ✅ 保持编辑器期望的格式：isLargeFile + storageType
        const fileData = {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            operateRecordId: operateRecordId,
            isLargeFile: true,
            storageType: 'indexedDB',
        };

        try {
            // 统一使用 IndexedDB 存储
            await saveLargeFileToIndexedDB(String(operateRecordId), file);
            console.log('[UploadFileHandler] 文件已保存到 IndexedDB:', file.name, '大小:', file.size);

            // ✅ 使用编辑器期望的键名 editor_upload_file_data
            sessionStorage.setItem('editor_upload_success', 'true');
            sessionStorage.setItem('editor_upload_file_data', JSON.stringify(fileData));
        } catch (error) {
            console.error('[UploadFileHandler] 文件存储失败', error);
            Toast('文件存储失败，请重试');
            throw error;
        }
    }

    // 立即跳转，不延迟
    pushEditPage(operateRecordId)
}

// ✅ 完成进度
const completeProgress = () => {
  console.log('[Progress] 完成阶段')
  if (isCompleting.value) return

  isCompleting.value = true
  setTargetProgress(100)

  // ✅ 延迟关闭弹窗，让用户看到100%
  setTimeout(() => {
    onCancel()  // 只关闭弹窗，不跳转页面
    emit('onConfirm')
  }, 1500)
}

// ✅ PDF渲染完成
const handlePDFRenderComplete = () => {
  console.log('[Progress] PDF渲染完成事件', {
    isDownCompleted,
    isPDFRenderComplete,
    isWasmLoadComplete,
    currentProgress: showPercentage.value
  })
  isPDFRenderComplete = true

  // ✅ 修改：需要等待 WASM 加载、文件下载和 PDF 渲染都完成
  if (isDownCompleted && isWasmLoadComplete) {
    console.log('[Progress] WASM、下载和渲染都完成，调用 completeProgress')
    completeProgress()
  } else {
    console.log('[Progress] 等待其他条件:', {
      isDownCompleted,
      isWasmLoadComplete
    })
  }
}

// ✅ 下载进度
const handleDownloadProgress = (progress: number) => {
  console.log('[Progress] 下载进度事件:', progress, '%', {
    isDownCompleted,
    isPDFRenderComplete,
    isWasmLoadComplete,
    currentProgress: showPercentage.value
  })

  // ✅ 真实进度到来，停止假进度
  stopFakeDownloadProgress()

  // ✅ 下载进度映射到 30-50%
  const downloadRange = PROGRESS_CONFIG.DOWNLOAD_END - PROGRESS_CONFIG.UPLOAD_END
  const newProgress = PROGRESS_CONFIG.UPLOAD_END + (progress / 100) * downloadRange
  setTargetProgress(newProgress)

  if (progress >= 100) {
    console.log('[Progress] 下载完成，设置 isDownCompleted = true')
    isDownCompleted = true

    // ✅ 确保进度到达 50%
    setTargetProgress(PROGRESS_CONFIG.DOWNLOAD_END)

    // ✅ 主动检查 WASM 状态（可能已经加载完成但事件丢失了）
    if (!isWasmLoadComplete && window.Module?.loadWasm) {
      console.log('[Progress] 检测到 WASM 已加载，手动设置完成状态')
      isWasmLoadComplete = true
      setTargetProgress(PROGRESS_CONFIG.WASM_END)
    }

    // ✅ 检查是否所有条件都满足
    if (isPDFRenderComplete && isWasmLoadComplete) {
      console.log('[Progress] 所有条件满足，调用 completeProgress')
      completeProgress()
    } else {
      console.log('[Progress] 等待其他条件:', {
        isPDFRenderComplete,
        isWasmLoadComplete
      })

      // ✅ 添加兜底机制：如果 2 秒后还没完成，强制检查并完成
      setTimeout(() => {
        if (isDownCompleted && !isCompleting.value) {
          console.log('[Progress] 兜底检查：下载已完成但进度未完成')

          // 再次检查 WASM
          if (!isWasmLoadComplete && window.Module?.loadWasm) {
            console.log('[Progress] 兜底：WASM 已加载，设置完成')
            isWasmLoadComplete = true
            setTargetProgress(PROGRESS_CONFIG.WASM_END)
          }

          // 如果所有条件都满足，完成进度
          if (isPDFRenderComplete && isWasmLoadComplete) {
            console.log('[Progress] 兜底：所有条件满足，完成进度')
            completeProgress()
          } else {
            console.log('[Progress] 兜底：仍在等待', {
              isPDFRenderComplete,
              isWasmLoadComplete
            })
          }
        }
      }, 2000)
    }
  }
}

const handleChangeFileName = (name: string) => {
  fileName.value = name
}

// ✅ 新增：处理文件切换（在 editor 页面切换操作记录时）
let fileUpdateTimer: number | null = null
const handleFileUpdate = () => {
  console.log('[Progress] 检测到文件切换事件')

  // ✅ 清除之前的定时器
  if (fileUpdateTimer) {
    clearTimeout(fileUpdateTimer)
    fileUpdateTimer = null
  }

  // ✅ 如果弹窗没有打开，不需要处理
  if (!visible.value) {
    console.log('[Progress] 弹窗未打开，忽略文件切换')
    return
  }

  // ✅ 如果进度已经很高（>80%），说明可能是旧文件的进度，需要重置
  // 如果进度很低（<20%），说明是新文件刚开始，也需要重置
  // 如果进度在中间（20-80%），可能是正在下载，延迟处理
  const currentProgress = showPercentage.value

  if (currentProgress > 80 || currentProgress < 20) {
    console.log('[Progress] 当前进度:', currentProgress, '%, 立即重置')
    resetProgressState()
  } else {
    console.log('[Progress] 当前进度:', currentProgress, '%, 延迟重置')
    // 延迟重置，等待当前下载完成
    fileUpdateTimer = window.setTimeout(() => {
      // 再次检查进度，如果还是卡在中间，说明确实需要重置
      if (showPercentage.value < 90 && !isDownCompleted) {
        console.log('[Progress] 延迟后进度仍未完成，执行重置')
        resetProgressState()
      }
      fileUpdateTimer = null
    }, 500)
  }
}

// ✅ 重置进度状态的辅助函数
const resetProgressState = () => {
  console.log('[Progress] 重置进度状态（文件切换）')

  // ✅ 关键修改：不要重置 WASM 和 PDF 渲染状态
  // 因为切换文件时，WASM 已经加载好了（缓存），PDF 也可能已经渲染好了
  // 只重置下载相关的状态

  // 只重置下载状态
  isDownCompleted = false
  isCompleting.value = false

  // 重置进度到下载阶段的起点（30%）
  currentDisplayProgress = PROGRESS_CONFIG.UPLOAD_END
  targetProgress = PROGRESS_CONFIG.UPLOAD_END
  showPercentage.value = PROGRESS_CONFIG.UPLOAD_END

  // 重新启动假进度
  stopFakeDownloadProgress()
  startFakeDownloadProgress()

  console.log('[Progress] 状态重置完成，保留 WASM 和 PDF 渲染状态', {
    isPDFRenderComplete,
    isWasmLoadComplete
  })
}

// ✅ 新增：WASM 加载进度处理
const handleWasmLoadProgress = (progress: number) => {
  console.log('[Progress] WASM 加载:', progress, '%')

  // ✅ WASM 加载进度映射到 50-90%
  const wasmRange = PROGRESS_CONFIG.WASM_END - PROGRESS_CONFIG.DOWNLOAD_END
  const wasmProgress = PROGRESS_CONFIG.DOWNLOAD_END + (progress / 100) * wasmRange
  setTargetProgress(wasmProgress)
}

// ✅ 新增：WASM 加载完成处理
const handleWasmLoadComplete = () => {
  console.log('[Progress] WASM 加载完成事件', {
    isDownCompleted,
    isPDFRenderComplete,
    isWasmLoadComplete,
    currentProgress: showPercentage.value
  })
  isWasmLoadComplete = true

  // ✅ WASM 加载完成后，进度到 90%
  setTargetProgress(PROGRESS_CONFIG.WASM_END)

  // ✅ 检查是否所有条件都满足
  if (isDownCompleted && isPDFRenderComplete) {
    console.log('[Progress] WASM、下载和渲染都完成，调用 completeProgress')
    completeProgress()
  } else {
    console.log('[Progress] 等待其他条件:', {
      isDownCompleted,
      isPDFRenderComplete
    })
  }
}


const route = useRoute();

// 是否来自外部网站跳转（doclingo / doclingo_enterprise），用于隐藏 OCR 和下载下拉入口
const isExternalSource = computed(() => {
  const source = route.query.source;
  return source === 'doclingo' || source === 'doclingo_enterprise';
});

onUnmounted(() => {
    eventBus.off("onUploadProgress", handleUploadProgress)
    eventBus.off(EventRenderProgressMessage.renderProgressComplete, handlePDFRenderComplete)
    eventBus.off(EventHomeMessage.changeFileName, handleChangeFileName)
    eventBus.off("file:downloadProgress", handleDownloadProgress)
    eventBus.off(EventRenderProgressMessage.wasmLoadProgress, handleWasmLoadProgress)
    eventBus.off(EventRenderProgressMessage.wasmLoadComplete, handleWasmLoadComplete)
    eventBus.off("ocr:fileUpdate", handleFileUpdate)
    stopSmoothProgress()
    stopFakeDownloadProgress()
})

onMounted(() => {
    eventBus.on("onUploadProgress", handleUploadProgress)
    eventBus.on(EventRenderProgressMessage.renderProgressComplete, handlePDFRenderComplete)
    eventBus.on(EventHomeMessage.changeFileName, handleChangeFileName)
    eventBus.on("file:downloadProgress", handleDownloadProgress)
    eventBus.on(EventRenderProgressMessage.wasmLoadProgress, handleWasmLoadProgress)
    eventBus.on(EventRenderProgressMessage.wasmLoadComplete, handleWasmLoadComplete)
    eventBus.on("ocr:fileUpdate", handleFileUpdate)

    // ✅ 延迟检查 WASM 状态，处理事件时序问题
    // 当弹窗打开时，WASM 可能已经加载完成但事件已经发送过了
    setTimeout(() => {
        if (visible.value && !isWasmLoadComplete && window.Module?.loadWasm) {
            console.log('[Progress] 检测到 WASM 已加载，手动触发完成')
            handleWasmLoadProgress(100)
            handleWasmLoadComplete()
        }
    }, 300)
})

defineExpose({
    open,
    close
})

</script>
<style lang="scss" scoped>
@use './index.scss' as *;
@use './mobile.scss' as *;
.upload-file-popup-dialog{
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0 !important;
}
</style>